import {
  Children,
  forwardRef,
  type ElementType,
  type ReactElement,
} from 'react';

import { Button } from '@dynatrace/strato-components/buttons';
import type {
  AriaLabelingProps,
  DataTestId,
  MaskingProps,
  PolymorphicComponentProps,
  StylingProps,
  WithChildren,
} from '@dynatrace/strato-components/core';
import { Text } from '@dynatrace/strato-components/typography';
import { getAppName } from '@dynatrace-sdk/app-environment';

import { AppIcon, type AppIconProps } from './app-icon/AppIcon.js';
import * as styles from './Logo.sty.js';
import { isSlot } from '../../core/utils/is-slot.js';

/**
 * @public
 */
export interface AppHeaderLogoOwnProps
  extends DataTestId,
    MaskingProps,
    StylingProps,
    AriaLabelingProps,
    WithChildren {
  appName?: string;
  appIcon?: string;
  href?: string;
}

// only exported for backwards compatibility, until deprecated
/**
 * @public
 */
export interface AppNavLinkInternalOwnProps extends AppHeaderLogoOwnProps {
  /** Has no effect. Will be deprecated. */
  pin?: boolean;
}

/**
 * @public
 */
export type AppHeaderLogoProps<E extends ElementType> =
  PolymorphicComponentProps<E, AppHeaderLogoOwnProps>;

/**
 * App logo with icon and app name
 * @public
 */
export const Logo: <E extends ElementType = 'a'>(
  props: AppHeaderLogoProps<E>,
) => ReactElement | null = forwardRef(
  <E extends ElementType>(
    props: AppHeaderLogoProps<E>,
    ref: typeof props.ref,
  ) => {
    const {
      appIcon,
      appName,
      as: propAs,
      href = '/',
      className,
      style,
      'data-testid': dataTestId,
      ...rest
    }: AppHeaderLogoProps<E> = props;
    const as = propAs ?? 'a';
    const name = appName ?? getAppName();

    let icon: ReactElement<AppIconProps> | undefined;
    for (const child of Children.toArray(props.children)) {
      if (isSlot(child, AppIcon)) {
        icon = child;
      }
    }

    return (
      <Button<ElementType>
        {...rest}
        data-testid={dataTestId}
        as={as}
        ref={ref}
        href={href}
        className={className}
        style={style}
      >
        <Button.Prefix>
          <AppIcon src={appIcon ?? icon?.props.src} alt="" />
        </Button.Prefix>
        <Text textStyle="base-emphasized" className={styles.title}>
          {name}
        </Text>
      </Button>
    );
  },
);

(Logo as typeof Logo & { displayName: string }).displayName = 'AppHeader.Logo';
